/**
 * AdminPlyxSellers — Plyx seller application review queue for admins.
 *
 * Route: `/admin/plyx/sellers`
 *
 * Lists pending applications by default, with filter pills for approved /
 * rejected / all. Each row exposes Approve and Reject (with reason) actions.
 * Auth: requires the AfriMarket admin JWT (admin@afrimarket.org or
 * admin@plyx.app — both have role=admin).
 */
import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Loader2, CheckCircle2, XCircle, ArrowLeft, FileText, ExternalLink,
  ShieldCheck, RefreshCw, AlertCircle,
} from "lucide-react";
import { toast } from "sonner";

const FILTERS = [
  { id: "pending", label: "Pending" },
  { id: "approved", label: "Approved" },
  { id: "rejected", label: "Rejected" },
  { id: "all", label: "All" },
];

const AdminPlyxSellers = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending");

  const reload = () => {
    setLoading(true);
    api
      .get(`/admin/plyx/sellers/pending?status=${statusFilter}`)
      .then((r) => setItems(r.data?.applications || []))
      .catch((err) => {
        const msg = err.response?.status === 403 ? "Admin access required" : "Could not load applications";
        toast.error(msg);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== "admin") {
      navigate("/admin", { replace: true });
      return;
    }
    reload();
  }, [authLoading, user, statusFilter]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="admin-plyx-sellers-page">
      <div className="max-w-4xl mx-auto px-5 pt-6 pb-24">
        <Link to="/admin/plyx" className="inline-flex items-center gap-1 text-sm text-white/60 hover:text-white mb-4">
          <ArrowLeft className="w-4 h-4"/> admin · plyx
        </Link>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-[#FFB42D]"/>
          </div>
          <div>
            <h1 className="font-display text-3xl tracking-tight">Seller applications</h1>
            <p className="text-xs text-white/55">
              Review Plyx seller verification submissions
            </p>
          </div>
          <button
            type="button"
            onClick={reload}
            className="ml-auto text-xs text-white/60 hover:text-white inline-flex items-center gap-1"
            data-testid="admin-plyx-sellers-refresh"
          >
            <RefreshCw className="w-3.5 h-3.5"/> refresh
          </button>
        </div>

        {/* Filter pills */}
        <div className="flex items-center gap-2 mb-5 overflow-x-auto pb-1">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setStatusFilter(f.id)}
              data-testid={`admin-plyx-sellers-filter-${f.id}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap ${
                statusFilter === f.id
                  ? "bg-white text-black"
                  : "bg-white/10 text-white/70 hover:bg-white/15"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin opacity-60"/>
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-white/5 p-10 text-center" data-testid="admin-plyx-sellers-empty">
            <AlertCircle className="w-8 h-8 mx-auto text-white/30 mb-3"/>
            <div className="font-semibold">No {statusFilter} applications</div>
            <div className="text-xs text-white/50 mt-1">
              Switch tabs above to see other states.
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((app) => (
              <SellerRow key={app.app_id} app={app} onChanged={reload}/>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const SellerRow = ({ app, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [reason, setReason] = useState("");

  const approve = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/plyx/sellers/${app.app_id}/approve`);
      toast.success(`Approved @${app.handle}`);
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Approve failed");
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    if (!reason.trim()) { toast.error("Add a reason so the creator can fix and re-apply."); return; }
    setBusy(true);
    try {
      await api.post(`/admin/plyx/sellers/${app.app_id}/reject`, { reason: reason.trim() });
      toast.success(`Rejected @${app.handle}`);
      setShowReject(false);
      setReason("");
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reject failed");
    } finally {
      setBusy(false);
    }
  };

  const statusPill = {
    pending:  "bg-[#FFB42D]/20 text-[#FFB42D]",
    approved: "bg-[#00E0C7]/20 text-[#00E0C7]",
    rejected: "bg-[#FF2D7A]/25 text-[#FF8AB5]",
  }[app.status] || "bg-white/10 text-white";

  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-4" data-testid={`admin-plyx-seller-row-${app.app_id}`}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Link to={`/plyx/u/${app.handle}`} className="font-semibold text-base hover:underline">
              @{app.handle}
            </Link>
            <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full ${statusPill}`}>
              {app.status}
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-xs text-white/70">
            <div><span className="text-white/45">Legal name:</span> <span className="text-white/90">{app.legal_name}</span></div>
            <div><span className="text-white/45">Country:</span> <span className="text-white/90">{app.country}</span></div>
            {app.business_name && (
              <div><span className="text-white/45">Business:</span> <span className="text-white/90">{app.business_name}</span></div>
            )}
            <div><span className="text-white/45">Submitted:</span> <span className="text-white/90">{new Date(app.submitted_at).toLocaleDateString()}</span></div>
            {app.reviewed_at && (
              <div><span className="text-white/45">Reviewed:</span> <span className="text-white/90">{new Date(app.reviewed_at).toLocaleDateString()}</span></div>
            )}
            {app.reject_reason && (
              <div className="col-span-full"><span className="text-white/45">Reject reason:</span> <span className="text-[#FF8AB5]">{app.reject_reason}</span></div>
            )}
          </div>
          <a
            href={app.id_doc_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`admin-plyx-seller-iddoc-${app.app_id}`}
            className="mt-2 inline-flex items-center gap-1 text-xs text-[#FFB42D] hover:text-[#FFD37A]"
          >
            <FileText className="w-3 h-3"/> view ID document <ExternalLink className="w-3 h-3"/>
          </a>
        </div>
      </div>

      {app.status === "pending" && (
        <div className="mt-4 pt-4 border-t border-white/10">
          {showReject ? (
            <div className="space-y-2" data-testid={`admin-plyx-seller-reject-form-${app.app_id}`}>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Reason (visible to creator)"
                className="w-full rounded-md bg-black/40 border border-white/15 px-3 py-2 text-sm outline-none focus:border-[#FF2D7A]"
                maxLength={400}
              />
              <div className="flex gap-2">
                <button type="button" onClick={reject} disabled={busy}
                  data-testid={`admin-plyx-seller-confirm-reject-${app.app_id}`}
                  className="flex-1 h-9 rounded-md bg-[#FF2D7A] text-white text-xs font-semibold disabled:opacity-50">
                  Confirm reject
                </button>
                <button type="button" onClick={() => { setShowReject(false); setReason(""); }}
                  className="flex-1 h-9 rounded-md bg-white/10 text-white text-xs font-semibold">
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="flex gap-2">
              <button type="button" onClick={approve} disabled={busy}
                data-testid={`admin-plyx-seller-approve-${app.app_id}`}
                className="flex-1 h-9 rounded-md bg-[#00E0C7] text-black text-xs font-semibold inline-flex items-center justify-center gap-1.5 disabled:opacity-50">
                <CheckCircle2 className="w-3.5 h-3.5"/> Approve
              </button>
              <button type="button" onClick={() => setShowReject(true)} disabled={busy}
                data-testid={`admin-plyx-seller-reject-${app.app_id}`}
                className="flex-1 h-9 rounded-md border border-[#FF2D7A]/40 text-[#FF8AB5] text-xs font-semibold inline-flex items-center justify-center gap-1.5">
                <XCircle className="w-3.5 h-3.5"/> Reject
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminPlyxSellers;
